import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import imageCompression from 'browser-image-compression';
import { todayLocal } from '../lib/dateHelpers';
import { uploadFieldReportPhoto, uploadFieldReportSignature } from '../lib/fieldReportUploads';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import JobSelectionCard from '../components/JobSelectionCard';
import { useFieldFormOptions } from '../hooks/useFieldFormOptions';
import LastSavedIndicator from '../components/LastSavedIndicator';
import { 
  Save, 
  Send, 
  Plus, 
  Trash2, 
  Camera, 
  MapPin, 
  Check, 
  X, 
  Minus,
  Loader2,
  ArrowLeft,
  FileText,
  AlertCircle,
  AlertTriangle,
  Download,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  computeFieldReportPerms,
  photoReadinessLabel,
} from '../utils/fieldReportWorkflow';

const API = process.env.REACT_APP_BACKEND_URL;

// Options for dropdowns
const UTILITY_TYPES = [
  "Electric (Power)", "Fiber Optic", "Communications (Copper/Telecom)", "Water", 
  "Sewer (Sanitary)", "Storm Drain", "Gas", "Duct Bank", "Irrigation", 
  "Fire Line", "Traffic Signal", "Street Lighting", "Unknown"
];

const UTILITY_MATERIALS = [
  "Conduit", "PVC", "HDPE", "Steel", "Cast Iron", "Ductile Iron", 
  "Copper", "Concrete", "Clay/Terra Cotta", "Asbestos Cement", "Composite/Other", "Unknown"
];

const UTILITY_COLORS = [
  { value: "Pink", color: "#ec4899" },
  { value: "Red", color: "#ef4444" },
  { value: "Yellow", color: "#eab308" },
  { value: "Orange", color: "#f97316" },
  { value: "Blue", color: "#3b82f6" },
  { value: "Green", color: "#22c55e" },
  { value: "Purple", color: "#a855f7" },
  { value: "White", color: "#f8fafc" }
];

const PORTION_EXPOSED = ["Full", "Half", "Side", "Top"];
const OBSERVED_CONDITIONS = ["Good", "Fair", "Poor", "Damaged", "Abandoned/Out of Service", "Unknown"];
const SURFACE_COVERINGS = ["Soil", "Gravel", "Asphalt", "Concrete"];
const SOIL_CONDITIONS = ["Soft", "Loose", "Compact", "Hard", "Rocky", "Sandy", "Clay", "Wet/Saturated", "Frozen", "Mixed/Variable"];

// Photo status toggle component
function PhotoStatusToggle({ value, onChange, disabled }) {
  return (
    <div className="flex gap-1">
      <Button
        type="button"
        size="sm"
        variant={value === 'pass' ? 'default' : 'outline'}
        className={value === 'pass' ? 'bg-green-600 hover:bg-green-700' : ''}
        onClick={() => onChange('pass')}
        disabled={disabled}
      >
        <Check className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant={value === 'fail' ? 'default' : 'outline'}
        className={value === 'fail' ? 'bg-red-600 hover:bg-red-700' : ''}
        onClick={() => onChange('fail')}
        disabled={disabled}
      >
        <X className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant={value === 'na' ? 'default' : 'outline'}
        className={value === 'na' ? 'bg-gray-600 hover:bg-gray-700' : ''}
        onClick={() => onChange('na')}
        disabled={disabled}
      >
        NA
      </Button>
    </div>
  );
}

// Photo upload component
function PhotoUpload({ label, photoUrl, status, onPhotoChange, onStatusChange, required, disabled, onUploadFile }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const isImg = (file.type || '').startsWith('image/');
      let uploadFile = file;
      if (isImg) {
        // Compress images using the same options Locate / PreCon use —
        // single source of truth across all three field forms.
        const options = {
          maxSizeMB: 0.3,
          maxWidthOrHeight: 1280,
          initialQuality: 0.82,
          useWebWorker: true,
          fileType: 'image/jpeg'
        };
        const compressedBlob = await imageCompression(file, options);
        uploadFile = new File([compressedBlob], file.name, { type: compressedBlob.type || 'image/jpeg', lastModified: Date.now() });
      }
      // Non-image attachments (PDF / DOC) upload as-is.
      const url = await onUploadFile(uploadFile);
      if (url) onPhotoChange(url);
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
        <PhotoStatusToggle value={status} onChange={onStatusChange} disabled={disabled} />
      </div>
      
      <div 
        className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
          ${photoUrl ? 'border-green-300 bg-green-50' : 'border-slate-300 hover:border-amber-500 hover:bg-amber-50'}`}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.doc,.docx,.heic,.heif"
          className="hidden"
          onChange={handleFileSelect}
          disabled={disabled}
        />

        {/* Delete X overlay — visible only when a photo / file is loaded
            and the form isn't read-only. Mirrors LocateForm's pattern.
            stopPropagation so clicking the X never re-opens the file
            picker behind it. */}
        {photoUrl && !disabled && !uploading && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPhotoChange(''); }}
            className="absolute top-2 right-2 z-10 bg-white border border-red-200 hover:bg-red-50 text-red-500 rounded-md p-1 shadow-sm"
            title="Remove attachment"
            data-testid={`pu-delete-${label}`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
        
        {uploading ? (
          <div className="py-4">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-amber-500" />
            <p className="text-xs text-slate-500 mt-1">Uploading to OneDrive…</p>
          </div>
        ) : photoUrl ? (
          /^https?:.+\.(pdf|docx?|xlsx?)(\?|$)/i.test(photoUrl) ? (
            <a
              href={photoUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-900 underline text-sm py-6"
              data-testid={`pu-file-link-${label}`}
            >
              <FileText className="w-5 h-5" />
              Open uploaded file
            </a>
          ) : (
            <img src={photoUrl} alt={label} className="max-h-72 mx-auto rounded" loading="lazy" decoding="async" />
          )
        ) : (
          <div className="py-4">
            <Camera className="w-10 h-10 mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600">Tap to take a photo (JPG / PNG / HEIC) or upload a PDF / DOC</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Signature pad component
function SignaturePad({ label, signatureUrl, onSign, gpsData, name, onNameChange, signedAt, required, disabled, onUploadSignature }) {
  const sigRef = useRef(null);
  const [showPad, setShowPad] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const captureSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Please sign before capturing');
      return;
    }

    setCapturing(true);

    // Read the canvas FIRST, before requesting GPS. The one-time native
    // "Allow location?" browser prompt sits directly over this button
    // row and a leaked dismiss-tap can land on "Clear" — reading the
    // signature now (and disabling Clear below) closes that timing gap.
    const dataUrl = sigRef.current.toDataURL('image/png');

    // Get GPS
    let gps = { lat: null, lng: null, accuracy: null };
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { 
          enableHighAccuracy: true, 
          timeout: 10000 
        });
      });
      gps = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      };
    } catch (err) {
      console.warn('GPS not available:', err);
    }

    try {
      // Upload signature to OneDrive immediately; store only the returned URL
      const uploadedUrl = await onUploadSignature(dataUrl);
      if (!uploadedUrl) throw new Error('Upload failed');
      onSign(uploadedUrl, gps);
      setShowPad(false);
    } catch (err) {
      console.error('Signature upload error:', err);
      toast.error('Failed to upload signature');
    } finally {
      setCapturing(false);
    }
  };

  const clearSignature = () => {
    // Second safety net: once capture has started, the canvas has
    // already been read — never let a leaked tap wipe it mid-upload.
    if (capturing) return;
    if (sigRef.current) {
      sigRef.current.clear();
    }
  };

  return (
    <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
      <Label className="text-sm font-semibold">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      
      <div className="space-y-2">
        <Input
          placeholder="Name"
          value={name || ''}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={disabled}
        />
        
        {signedAt && (
          <p className="text-xs text-slate-500">
            Signed: {new Date(signedAt).toLocaleString()}
          </p>
        )}
        
        {/* GPS display removed per user request */}
      </div>

      {signatureUrl ? (
        <div className="border rounded-lg p-2 bg-white">
          <img src={signatureUrl} alt="Signature" className="max-h-24 mx-auto" />
          {!disabled && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full mt-2"
              onClick={() => {
                onSign(null, null);
                setShowPad(true);
              }}
            >
              Re-sign
            </Button>
          )}
        </div>
      ) : (
        <>
          {showPad ? (
            <div className="space-y-2">
              <div className="border-2 border-slate-300 rounded-lg bg-white touch-none">
                <SignatureCanvas
                  ref={sigRef}
                  canvasProps={{
                    className: 'w-full h-32',
                    style: { width: '100%', height: '128px' }
                  }}
                  backgroundColor="white"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={clearSignature} disabled={capturing} className="flex-1">
                  Clear
                </Button>
                <Button type="button" size="sm" onClick={captureSignature} disabled={capturing} className="flex-1 bg-green-600 hover:bg-green-700">
                  {capturing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Capture'}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setShowPad(true)}
              disabled={disabled}
            >
              <FileText className="w-4 h-4 mr-2" />
              Tap to Sign
            </Button>
          )}
        </>
      )}
    </div>
  );
}

// Reference Point Card
// Reference Points now accept 3-6 attachments per point (mixed images +
// PDF/DOC files), rendered with the same stacked AdditionalPhotoCard
// pattern Section D uses for consistency. Backward-compat: a legacy
// `photo_url` value migrates to `attachments[0]` on load so older records
// keep working. We also keep `photo_url` in sync with the first image
// attachment on every change so the existing backend PDF generator
// (which still reads `photo_url`) keeps working without modification.
const RP_MIN_ATTACHMENTS = 3;
const RP_MAX_ATTACHMENTS = 6;

function ReferencePointCard({ point, index, onChange, onRemove, canRemove, disabled, onUploadFile, onDeletePhoto }) {
  const attachments = Array.isArray(point.attachments) && point.attachments.length > 0
    ? point.attachments
    : (point.photo_url ? [{ id: crypto.randomUUID(), url: point.photo_url, kind: 'image', name: '', caption: '' }] : []);

  const updateAttachments = (next) => {
    const firstImage = next.find((a) => a.kind === 'image' && a.url) || next.find((a) => a.url);
    onChange({ ...point, attachments: next, photo_url: firstImage ? firstImage.url : '' });
  };

  const updateAttachment = (idx, data) => updateAttachments(attachments.map((a, i) => (i === idx ? data : a)));
  const removeAttachment = (idx) => {
    // If the attachment had an uploaded file, ask the backend to clean
    // it out of OneDrive too. Without this, the file would linger and
    // appear as a false "orphan" in /admin/photo-audit.
    const removed = attachments[idx];
    if (removed && onDeletePhoto) onDeletePhoto(removed);
    updateAttachments(attachments.filter((_, i) => i !== idx));
  };
  const addAttachment = () => {
    if (attachments.length >= RP_MAX_ATTACHMENTS) return;
    updateAttachments([...attachments, { id: crypto.randomUUID(), url: '', kind: 'image', name: '', caption: '' }]);
  };

  const filledCount = attachments.filter((a) => a.url).length;
  const belowMin = filledCount < RP_MIN_ATTACHMENTS;

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-['Barlow_Condensed']">Reference Point #{index + 1}</CardTitle>
          {canRemove && !disabled && (
            <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Attachments — 3 minimum, 6 maximum. Reuses Section D's
            AdditionalPhotoCard for visual + behavioural consistency. */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">
              Photos / Files <span className="text-red-500">*</span>
              <span className={`ml-2 text-xs font-normal ${belowMin ? 'text-amber-600' : 'text-slate-500'}`}>
                ({filledCount} of {RP_MIN_ATTACHMENTS}-{RP_MAX_ATTACHMENTS})
              </span>
            </Label>
          </div>
          {attachments.map((att, idx) => (
            <AdditionalPhotoCard
              key={att.id || idx}
              photo={att}
              onChange={(data) => updateAttachment(idx, data)}
              onRemove={() => removeAttachment(idx)}
              disabled={disabled}
              onUploadFile={onUploadFile}
            />
          ))}
          {!disabled && attachments.length < RP_MAX_ATTACHMENTS && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={addAttachment}
              data-testid={`rp-add-attachment-${index}`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Photo / File
            </Button>
          )}
          {belowMin && (
            <p className="text-xs text-amber-600">
              At least {RP_MIN_ATTACHMENTS} attachments are required before submitting this report.
            </p>
          )}
        </div>

        {/* Location */}
        <div>
          <Label className="text-sm">Location <span className="text-red-500">*</span></Label>
          <Input
            placeholder="e.g., NE corner of building"
            value={point.location_desc || ''}
            onChange={(e) => onChange({ ...point, location_desc: e.target.value })}
            disabled={disabled}
          />
        </div>

        {/* Street & Cross Street */}
        <div>
          <Label className="text-sm">Street & Cross Street <span className="text-red-500">*</span></Label>
          <Input
            placeholder="e.g., Main St & 1st Ave"
            value={point.street_cross || ''}
            onChange={(e) => onChange({ ...point, street_cross: e.target.value })}
            disabled={disabled}
          />
        </div>

        {/* Distance */}
        <div>
          <Label className="text-sm">Distance to Reference Point <span className="text-red-500">*</span></Label>
          <div className="flex gap-2 items-center">
            <Input
              type="number"
              placeholder="Feet"
              min="0"
              value={point.distance_feet ?? ''}
              onChange={(e) => onChange({ ...point, distance_feet: e.target.value ? parseInt(e.target.value) : null })}
              className="flex-1"
              disabled={disabled}
            />
            <span className="text-sm text-slate-500">ft</span>
            <Input
              type="number"
              placeholder="In"
              min="0"
              max="11"
              value={point.distance_inches ?? ''}
              onChange={(e) => onChange({ ...point, distance_inches: e.target.value ? parseInt(e.target.value) : null })}
              className="w-20"
              disabled={disabled}
            />
            <span className="text-sm text-slate-500">in</span>
          </div>
        </div>

        {/* Notes */}
        <div>
          <Label className="text-sm">Additional Notes</Label>
          <Textarea
            placeholder="Optional notes..."
            value={point.notes || ''}
            onChange={(e) => onChange({ ...point, notes: e.target.value })}
            rows={2}
            disabled={disabled}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// Additional Photo card with caption and status
function AdditionalPhotoCard({ photo, onChange, onRemove, disabled, onUploadFile }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const isImg = (file.type || '').startsWith('image/');
      let uploadFile = file;
      if (isImg) {
        const options = { maxSizeMB: 0.3, maxWidthOrHeight: 1280, initialQuality: 0.82, useWebWorker: true, fileType: 'image/jpeg' };
        const compressedBlob = await imageCompression(file, options);
        uploadFile = new File([compressedBlob], file.name, { type: compressedBlob.type || 'image/jpeg', lastModified: Date.now() });
      }
      const url = await onUploadFile(uploadFile);
      if (url) onChange({ ...photo, url, name: file.name });
    } catch (err) {
      console.error('Additional photo upload error:', err);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  return (
    <div className="border rounded-lg p-3 space-y-2 bg-white">
      <div className="flex items-center justify-between">
        <Label className="text-sm">Additional Photo or File</Label>
        <div className="flex items-center gap-2">
          <PhotoStatusToggle
            value={photo.status}
            onChange={(v) => onChange({ ...photo, status: v })}
            disabled={disabled}
          />
          {!disabled && (
            <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      </div>
      <div
        className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer ${photo.url ? 'border-green-300 bg-green-50' : 'border-slate-300 hover:border-amber-500 hover:bg-amber-50'}`}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.doc,.docx,.heic,.heif"
          className="hidden"
          onChange={handleFileSelect}
          disabled={disabled}
        />
        {uploading ? (
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-amber-500" />
        ) : photo.url ? (
          /^https?:.+\.(pdf|docx?|xlsx?)(\?|$)/i.test(photo.url) ? (
            <a
              href={photo.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-900 underline text-sm py-6"
            >
              <FileText className="w-5 h-5" />
              {photo.name || 'Open uploaded file'}
            </a>
          ) : (
            <img src={photo.url} alt="Additional" className="max-h-60 mx-auto rounded" loading="lazy" decoding="async" />
          )
        ) : (
          <div className="py-4">
            <Camera className="w-8 h-8 mx-auto text-slate-400 mb-1" />
            <p className="text-xs text-slate-600">Tap to add a photo (JPG / PNG / HEIC) or file (PDF / DOC)</p>
          </div>
        )}
      </div>
      <Textarea
        placeholder="Optional caption..."
        value={photo.caption || ''}
        onChange={(e) => onChange({ ...photo, caption: e.target.value })}
        rows={2}
        disabled={disabled}
      />
    </div>
  );
}

// Single Soft Dig Entry — encapsulates Sections B/C/D/E for one dig
function SoftDigEntryCard({ dig, index, onChange, onRemove, canRemove, disabled, totalCount, onUploadFile, onDeletePhoto }) {
  const updateDigField = (field, value) => onChange({ ...dig, [field]: value });

  // Editable option lists from /api/field-form-options. Hardcoded constants
  // are used as instant fallback while the fetch is in flight or if the
  // backend is unreachable. Any previously-saved values that are no longer
  // in the admin list show as "removed" badges (handled by the hook).
  const utilityTypeOptions = useFieldFormOptions(
    'softdig.utility_type', UTILITY_TYPES, dig.utility_type
  );
  const utilityMaterialOptions = useFieldFormOptions(
    'softdig.utility_material', UTILITY_MATERIALS, dig.utility_material
  );
  const colorCodeOptions = useFieldFormOptions(
    'softdig.utility_color_code', UTILITY_COLORS, dig.utility_color_code
  );
  const portionExposedOptions = useFieldFormOptions(
    'softdig.portion_exposed', PORTION_EXPOSED, dig.portion_exposed
  );
  const observedConditionOptions = useFieldFormOptions(
    'softdig.observed_condition', OBSERVED_CONDITIONS, dig.observed_condition
  );
  const surfaceCoveringOptions = useFieldFormOptions(
    'softdig.surface_covering', SURFACE_COVERINGS, dig.surface_covering
  );
  const soilConditionOptions = useFieldFormOptions(
    'softdig.soil_condition', SOIL_CONDITIONS, dig.soil_condition
  );

  const updateReferencePoint = (rpIndex, data) => {
    onChange({
      ...dig,
      reference_points: (dig.reference_points || []).map((p, i) => (i === rpIndex ? data : p))
    });
  };
  const addReferencePoint = () => {
    onChange({
      ...dig,
      reference_points: [
        ...(dig.reference_points || []),
        { id: crypto.randomUUID(), photo_url: '', photo_status: '', location_desc: '', street_cross: '', distance_feet: null, distance_inches: null, notes: '' }
      ]
    });
  };
  const removeReferencePoint = (rpIndex) => {
    onChange({ ...dig, reference_points: (dig.reference_points || []).filter((_, i) => i !== rpIndex) });
  };

  const additionalPhotos = dig.additional_photos || [];
  const updateAdditionalPhoto = (pIndex, data) => {
    onChange({ ...dig, additional_photos: additionalPhotos.map((p, i) => (i === pIndex ? data : p)) });
  };
  const addAdditionalPhoto = () => {
    onChange({
      ...dig,
      additional_photos: [...additionalPhotos, { id: crypto.randomUUID(), url: '', caption: '', status: '' }]
    });
  };
  const removeAdditionalPhoto = (pIndex) => {
    // Clean up the OneDrive file when an already-uploaded photo is
    // removed, otherwise it lingers as a false "orphan" in
    // /admin/photo-audit.
    const removed = additionalPhotos[pIndex];
    if (removed && onDeletePhoto) onDeletePhoto(removed);
    onChange({ ...dig, additional_photos: additionalPhotos.filter((_, i) => i !== pIndex) });
  };

  const isDuctBank = dig.utility_type === 'Duct Bank';

  return (
    <Card className="border-amber-200 border-2" data-testid={`soft-dig-entry-${index}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-['Barlow_Condensed']">
            Soft Dig Detail #{index + 1}
            {totalCount > 1 && (
              <span className="ml-2 text-xs text-slate-500 font-normal">of {totalCount}</span>
            )}
          </CardTitle>
          {canRemove && !disabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRemove}
              data-testid={`remove-soft-dig-${index}-btn`}
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Section B: Soft Dig Details */}
        <div className="space-y-4">
          <h4 className="font-semibold text-slate-800 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">B</span>
            Soft Dig Details
          </h4>
          <div>
            <Label>Soft Dig Number <span className="text-red-500">*</span></Label>
            <Input
              value={dig.soft_dig_number || ''}
              onChange={(e) => updateDigField('soft_dig_number', e.target.value)}
              placeholder="e.g., SD-001"
              disabled={disabled}
            />
          </div>
          <div>
            <Label>Utility Type <span className="text-red-500">*</span></Label>
            <MultiSelectDropdown
              options={utilityTypeOptions}
              value={dig.utility_type}
              onChange={(arr) => updateDigField('utility_type', arr)}
              disabled={disabled}
              testIdBase={`dig-utility-type`}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Utility Owner (if known)</Label>
            <Input
              value={dig.utility_owner || ''}
              onChange={(e) => updateDigField('utility_owner', e.target.value)}
              placeholder="Optional"
              disabled={disabled}
            />
          </div>
          <div>
            <Label>Utility Size (inches)</Label>
            <Input
              type="text"
              value={dig.utility_size_inches ?? ''}
              onChange={(e) => updateDigField('utility_size_inches', e.target.value)}
              placeholder='Any size — e.g. 2, 2 1/4, 3 5/8'
              disabled={disabled}
            />
          </div>
          <div>
            <Label>Note</Label>
            <Textarea
              value={dig.note || ''}
              onChange={(e) => updateDigField('note', e.target.value)}
              placeholder="Optional notes"
              rows={2}
              disabled={disabled}
            />
          </div>
          <div>
            <Label>Utility Material Type <span className="text-red-500">*</span></Label>
            <MultiSelectDropdown
              options={utilityMaterialOptions}
              value={dig.utility_material}
              onChange={(arr) => updateDigField('utility_material', arr)}
              disabled={disabled}
              testIdBase="dig-utility-material"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Utility Color Code Designation <span className="text-red-500">*</span></Label>
            <MultiSelectDropdown
              options={colorCodeOptions.map(c => ({ value: c.value, label: c.value, color: c.color, legacy: c.legacy }))}
              value={dig.utility_color_code}
              onChange={(arr) => updateDigField('utility_color_code', arr)}
              disabled={disabled}
              testIdBase="dig-color-code"
              className="mt-1"
              renderOption={(opt) => (
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-4 h-4 rounded-full border border-slate-300" style={{ backgroundColor: opt.color || '#e5e7eb' }} />
                  {opt.label}
                  {opt.legacy && (
                    <span className="ml-1 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1 rounded">
                      removed
                    </span>
                  )}
                </span>
              )}
            />
          </div>
          <div>
            <Label>Portion of Utility Exposed <span className="text-red-500">*</span></Label>
            <MultiSelectDropdown
              options={portionExposedOptions}
              value={dig.portion_exposed}
              onChange={(arr) => updateDigField('portion_exposed', arr)}
              disabled={disabled}
              testIdBase="dig-portion-exposed"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Observed Utility Condition <span className="text-red-500">*</span></Label>
            <MultiSelectDropdown
              options={observedConditionOptions}
              value={dig.observed_condition}
              onChange={(arr) => updateDigField('observed_condition', arr)}
              disabled={disabled}
              testIdBase="dig-observed-condition"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Surface Covering Type <span className="text-red-500">*</span></Label>
            <MultiSelectDropdown
              options={surfaceCoveringOptions}
              value={dig.surface_covering}
              onChange={(arr) => updateDigField('surface_covering', arr)}
              disabled={disabled}
              testIdBase="dig-surface-covering"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Soil Condition <span className="text-red-500">*</span></Label>
            <MultiSelectDropdown
              options={soilConditionOptions}
              value={dig.soil_condition}
              onChange={(arr) => updateDigField('soil_condition', arr)}
              disabled={disabled}
              testIdBase="dig-soil-condition"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Core Thickness (inches)</Label>
            <Input
              type="number"
              step="0.01"
              value={dig.core_thickness_inches ?? ''}
              onChange={(e) => updateDigField('core_thickness_inches', e.target.value)}
              placeholder="Optional"
              disabled={disabled}
              data-testid={`dig-core-thickness-inches-${index}`}
            />
          </div>
        </div>

        {/* Section C: Elevations */}
        <div className="rounded-lg border bg-slate-50 p-4 space-y-4">
          <h4 className="font-semibold text-slate-800 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">C</span>
            Elevations
          </h4>
          <div>
            <Label>Elevation From Existing Grade (inches) <span className="text-red-500">*</span></Label>
            <Input
              type="number"
              value={dig.elevation_from_grade_inches ?? ''}
              onChange={(e) => updateDigField('elevation_from_grade_inches', e.target.value)}
              placeholder="e.g., 44"
              disabled={disabled}
            />
          </div>
          {isDuctBank ? (
            <div>
              <Label>Elevation to Bottom of Utility (inches) <span className="text-red-500">*</span></Label>
              <Input
                type="number"
                value={dig.elevation_to_bottom_inches ?? ''}
                onChange={(e) => updateDigField('elevation_to_bottom_inches', e.target.value)}
                placeholder="Required for Duct Bank"
                disabled={disabled}
              />
            </div>
          ) : (
            <p className="text-sm text-slate-500 italic">
              Elevation to Bottom: N/A (only required for Duct Bank)
            </p>
          )}
        </div>

        {/* Section D: Photo Documentation */}
        <div className="rounded-lg border bg-slate-50 p-4 space-y-4">
          <h4 className="font-semibold text-slate-800 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">D</span>
            Photo Documentation
          </h4>
          <PhotoUpload
            label="Site Plan Soft Dig Location"
            photoUrl={dig.site_plan_photo_url}
            status={dig.site_plan_photo_status}
            onPhotoChange={(url) => updateDigField('site_plan_photo_url', url)}
            onStatusChange={(s) => updateDigField('site_plan_photo_status', s)}
            required
            disabled={disabled}
            onUploadFile={onUploadFile}
          />
          <PhotoUpload
            label="Image of Exposed Utility"
            photoUrl={dig.exposed_utility_photo_url}
            status={dig.exposed_utility_photo_status}
            onPhotoChange={(url) => updateDigField('exposed_utility_photo_url', url)}
            onStatusChange={(s) => updateDigField('exposed_utility_photo_status', s)}
            required
            disabled={disabled}
            onUploadFile={onUploadFile}
          />
          {additionalPhotos.map((photo, pIndex) => (
            <AdditionalPhotoCard
              key={photo.id}
              photo={photo}
              onChange={(data) => updateAdditionalPhoto(pIndex, data)}
              onRemove={() => removeAdditionalPhoto(pIndex)}
              disabled={disabled}
              onUploadFile={onUploadFile}
              onDeletePhoto={onDeletePhoto}
            />
          ))}
          {!disabled && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={addAdditionalPhoto}
              data-testid={`add-photo-dig-${index}-btn`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Photo
            </Button>
          )}
        </div>

        {/* Section E: Reference Points */}
        <div className="rounded-lg border bg-slate-50 p-4 space-y-4">
          <h4 className="font-semibold text-slate-800 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">E</span>
            Reference Points
          </h4>
          {(dig.reference_points || []).map((point, rpIndex) => (
            <ReferencePointCard
              key={point.id}
              point={point}
              index={rpIndex}
              onChange={(data) => updateReferencePoint(rpIndex, data)}
              onRemove={() => removeReferencePoint(rpIndex)}
              canRemove={(dig.reference_points || []).length > 1}
              disabled={disabled}
              onUploadFile={onUploadFile}
              onDeletePhoto={onDeletePhoto}
            />
          ))}
          {!disabled && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={addReferencePoint}
              data-testid={`add-reference-point-dig-${index}-btn`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Reference Point
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Main Form Component
export default function SoftDigForm() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, isAdmin } = useAuth();
  const isEditing = !!id;
  const isViewOnlyParam = searchParams.get('view') === 'true';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  // Counts photos currently mid-upload across all sub-components — every
  // call to uploadPhoto() below increments/decrements this, so Save
  // Draft/Submit can block until in-flight uploads land (2026-09-01 fix;
  // previously declared but never incremented, so a save could fire
  // before a just-added photo's upload had finished — the likely cause of
  // "photos disappeared" reports on Soft Dig, which has far more upload
  // points per form than Locate/PreCon).
  const [pendingUploads, setPendingUploads] = useState(0);
  // True when loadSoftDig hit a network/timeout/5xx error (not a real
  // 404/403) — shows an inline Retry instead of booting the tech back to
  // the Field Reports list on a bad-signal blip.
  const [loadError, setLoadError] = useState(false);
  // Track the form's id locally so newly-created drafts have an id we can use
  // for photo uploads even before navigation re-runs useParams.
  const [currentId, setCurrentId] = useState(id || null);
  
  // Job selection state
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Support both 'job' and 'job_id' query params for flexibility
  const jobIdFromParams = searchParams.get('job') || searchParams.get('job_id') || '';
  
  // Helper to construct an empty soft dig entry
  const makeEmptyDig = () => ({
    id: crypto.randomUUID(),
    soft_dig_number: '',
    utility_type: [],
    utility_owner: '',
    utility_size_inches: '',
    note: '',
    utility_material: [],
    utility_color_code: [],
    portion_exposed: [],
    observed_condition: [],
    surface_covering: [],
    soil_condition: [],
    core_thickness_inches: '',
    elevation_from_grade_inches: '',
    elevation_to_bottom_inches: '',
    site_plan_photo_url: '',
    site_plan_photo_status: '',
    exposed_utility_photo_url: '',
    exposed_utility_photo_status: '',
    additional_photos: [],
    reference_points: [
      { id: crypto.randomUUID(), photo_url: '', photo_status: '', location_desc: '', street_cross: '', distance_feet: null, distance_inches: null, notes: '' }
    ]
  });

  const [formData, setFormData] = useState({
    job_id: jobIdFromParams,
    job_number: '',
    assigned_team: '',
    site_location: '',
    date: todayLocal(),
    cll_lead_on_site: user?.full_name || '',
    // Multi-dig array (each contains B/C/D/E fields)
    soft_digs: [makeEmptyDig()],
    submitted_by: user?.full_name || '',
    submitter_name: user?.full_name || '',
    submitter_signed_at: '',
    submitter_signature_url: '',
    submitter_gps_lat: null,
    submitter_gps_lng: null,
    submitter_gps_accuracy: null,
    reviewer_name: '',
    reviewer_signed_at: '',
    reviewer_signature_url: '',
    reviewer_gps_lat: null,
    reviewer_gps_lng: null,
    reviewer_gps_accuracy: null,
    status: 'draft'
  });

  // Workflow permissions — mirrors backend /utils/field_report_workflow.py.
  const canApproveFieldReports = () => {
    if (isAdmin()) return true;
    const permissions = user?.permissions || {};
    return permissions.can_approve_field_reports === true;
  };

  const {
    isCompleted,
    isPending,
    isSubmitter,
    isAdminOrSuper,
    isViewOnly,
    submitterSignatureLocked,
    canAdminUnlock,
    isAdminEditingNonDraft,
  } = computeFieldReportPerms({
    formData,
    user,
    viewParamSet: isViewOnlyParam,
    canApprove: canApproveFieldReports(),
  });

  // Unlock the form so admin/approver can fix a mistake. Idempotent.
  const handleAdminEdit = () => {
    if (!isViewOnly) return;
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next, { replace: true });
  };

  // Save admin edits without touching status — reuses the same PUT path
  // submitters use for pending-fix edits.
  const handleAdminSaveEdits = async () => {
    await handleSaveSubmitterEdits();
  };

  const canAdminEdit = canApproveFieldReports() && isPending;
  const isSubmittedOrCompleted = isPending || isCompleted;
  const photoBannerText = photoReadinessLabel({
    expected: formData.expected_photos_count,
    received: formData.received_photos_count,
    photos_ready: formData.photos_ready,
  });
  const photosNotReady = pendingUploads > 0 || photoBannerText !== null;

  // Autosave timer
  const autosaveRef = useRef(null);
  // SAFETY: see LocateForm.js — block save-draft / autosave until the
  // server's data has actually been loaded into formData.
  const formLoadedRef = useRef(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Load available jobs on mount
  useEffect(() => {
    loadAvailableJobs();
  }, [token]);

  // Always start the form scrolled to the top (fixes "opens at bottom"
  // bug when navigating in from the bottom of a Job detail page).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-select job if job_id comes from URL params (when creating new report from Job page).
  // Fall back to a single targeted fetch if the job isn't in the active-job dropdown
  // (e.g., status = "Ready to schedule" or any non-active status the form would otherwise skip).
  useEffect(() => {
    if (isEditing || !jobIdFromParams || selectedJob || loadingJobs) return;
    const inList = availableJobs.find(j => j.id === jobIdFromParams);
    if (inList) {
      handleJobSelect(jobIdFromParams);
      return;
    }
    // Not in the active-job list — fetch this single job directly
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/jobs/${jobIdFromParams}/field-report-prefill`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled || !res.data?.id) return;
        const job = res.data;
        setAvailableJobs(prev => prev.some(j => j.id === job.id) ? prev : [...prev, job]);
        setSelectedJob(job);
        setFormData(prev => ({
          ...prev,
          job_id: job.id,
          job_number: job.job_number,
          site_location: job.service_address || '',
          assigned_team: job.assigned_team_names?.join(', ') || '',
          date: job.date || prev.date,
          cll_lead_on_site: user?.full_name || prev.cll_lead_on_site
        }));
      } catch (err) {
        console.warn('Prefill fetch failed for job', jobIdFromParams, err);
      }
    })();
    return () => { cancelled = true; };
  }, [availableJobs, jobIdFromParams, isEditing, selectedJob, loadingJobs]);

  // Load existing data
  useEffect(() => {
    if (isEditing && availableJobs.length > 0) {
      loadSoftDig();
    }
  }, [id, availableJobs]);

  // Autosave every 10 seconds for drafts
  useEffect(() => {
    if (!isEditing || formData.status !== 'draft' || isViewOnly) return;
    // SAFETY: don't start the autosave loop until the load has finished.
    if (!formLoadedRef.current) return;

    autosaveRef.current = setInterval(() => {
      handleSaveDraft(true);
    }, 10000);

    return () => {
      if (autosaveRef.current) {
        clearInterval(autosaveRef.current);
      }
    };
  }, [isEditing, formData.status, isViewOnly]);

  const loadAvailableJobs = async () => {
    setLoadingJobs(true);
    try {
      const response = await axios.get(`${API}/api/jobs/for-field-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableJobs(response.data || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast.error('Failed to load available jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleJobSelect = (jobId) => {
    const job = availableJobs.find(j => j.id === jobId);
    if (job) {
      setSelectedJob(job);
      // Auto-populate fields from job
      setFormData(prev => ({
        ...prev,
        job_id: job.id,
        job_number: job.job_number,
        site_location: job.service_address || '',
        assigned_team: job.assigned_team_names?.join(', ') || '',
        date: job.date || todayLocal(),
        cll_lead_on_site: user?.full_name || ''
      }));
    }
  };

  // Normalize legacy single-dig docs into the multi-dig shape
  const normalizeSoftDigPayload = (raw) => {
    const data = { ...raw };
    // Legacy fields that used to live at the top level
    const LEGACY_DIG_FIELDS = [
      'soft_dig_number', 'utility_type', 'utility_owner', 'utility_size_inches', 'note',
      'utility_material', 'utility_color_code', 'portion_exposed', 'observed_condition',
      'surface_covering', 'soil_condition', 'core_thickness_inches', 'elevation_from_grade_inches', 'elevation_to_bottom_inches',
      'site_plan_photo_url', 'site_plan_photo_status', 'exposed_utility_photo_url', 'exposed_utility_photo_status'
    ];
    if (!Array.isArray(data.soft_digs) || data.soft_digs.length === 0) {
      // Build a single dig from flat legacy fields
      const dig = { id: crypto.randomUUID(), additional_photos: [] };
      LEGACY_DIG_FIELDS.forEach((f) => { dig[f] = data[f] ?? ''; });
      dig.reference_points = Array.isArray(data.reference_points) && data.reference_points.length > 0
        ? data.reference_points
        : [{ id: crypto.randomUUID(), photo_url: '', photo_status: '', location_desc: '', street_cross: '', distance_feet: null, distance_inches: null, notes: '' }];
      data.soft_digs = [dig];
    } else {
      // Ensure each entry has id + arrays
      data.soft_digs = data.soft_digs.map((d) => ({
        id: d.id || crypto.randomUUID(),
        additional_photos: d.additional_photos || [],
        reference_points: (d.reference_points && d.reference_points.length > 0)
          ? d.reference_points
          : [{ id: crypto.randomUUID(), photo_url: '', photo_status: '', location_desc: '', street_cross: '', distance_feet: null, distance_inches: null, notes: '' }],
        ...d,
      }));
    }
    return data;
  };

  // Mirror the first dig's fields back to the top-level (legacy compatibility for old PDFs/preview).
  const buildSavePayload = (data) => {
    // Helper: empty string → null (Pydantic Optional[float] rejects "")
    const toNum = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n) ? n : null;
    };
    // Utility size can be a fraction like "2 1/4" — never coerce to Number.
    // Backend model was relaxed to Union[str, float] on Feb-2026 for exactly
    // this reason. Empty string → null so we don't store useless whitespace.
    const preserveSize = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      if (typeof v === 'number') return v;
      const trimmed = String(v).trim();
      return trimmed === '' ? null : trimmed;
    };
    const payload = { ...data };
    // Normalize numeric fields inside each soft_dig entry too
    if (Array.isArray(payload.soft_digs)) {
      payload.soft_digs = payload.soft_digs.map((d) => ({
        ...d,
        utility_size_inches: preserveSize(d.utility_size_inches),
        core_thickness_inches: toNum(d.core_thickness_inches),
        elevation_from_grade_inches: toNum(d.elevation_from_grade_inches),
        elevation_to_bottom_inches: toNum(d.elevation_to_bottom_inches),
      }));
    }
    const firstDig = (payload.soft_digs && payload.soft_digs[0]) || null;
    if (firstDig) {
      payload.soft_dig_number = firstDig.soft_dig_number || '';
      payload.utility_type = firstDig.utility_type || '';
      payload.utility_owner = firstDig.utility_owner || '';
      payload.utility_size_inches = preserveSize(firstDig.utility_size_inches);
      payload.note = firstDig.note || '';
      payload.utility_material = firstDig.utility_material || '';
      payload.utility_color_code = firstDig.utility_color_code || '';
      payload.portion_exposed = firstDig.portion_exposed || '';
      payload.observed_condition = firstDig.observed_condition || '';
      payload.surface_covering = firstDig.surface_covering || '';
      payload.soil_condition = firstDig.soil_condition || '';
      payload.core_thickness_inches = toNum(firstDig.core_thickness_inches);
      payload.elevation_from_grade_inches = toNum(firstDig.elevation_from_grade_inches);
      payload.elevation_to_bottom_inches = toNum(firstDig.elevation_to_bottom_inches);
      payload.site_plan_photo_url = firstDig.site_plan_photo_url || '';
      payload.site_plan_photo_status = firstDig.site_plan_photo_status || '';
      payload.exposed_utility_photo_url = firstDig.exposed_utility_photo_url || '';
      payload.exposed_utility_photo_status = firstDig.exposed_utility_photo_status || '';
      payload.reference_points = firstDig.reference_points || [];
    }
    return payload;
  };

  // Ensure we have a saved draft id before uploading any photos/signatures.
  // If not yet saved, create a minimal draft first so we have a stable folder.
  // RACE LOCK: multiple concurrent callers (rapid photo taps, autosave + photo)
  // all await the SAME in-flight POST to avoid duplicate drafts.
  const draftCreationRef = useRef(null);
  const ensureDraftId = async () => {
    if (currentId) return currentId;
    if (draftCreationRef.current) return draftCreationRef.current;
    draftCreationRef.current = (async () => {
      try {
        const payload = buildSavePayload({ ...formData, status: 'draft' });
        const res = await axios.post(`${API}/api/soft-digs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newId = res.data?.id;
        if (newId) {
          setCurrentId(newId);
          navigate(`/soft-dig/${newId}`, { replace: true });
          return newId;
        }
      } catch (err) {
        console.error('Failed to create draft for upload:', err);
        toast.error('Could not save draft before upload');
        draftCreationRef.current = null; // allow retry on failure
      }
      return null;
    })();
    return await draftCreationRef.current;
  };

  // Upload a compressed image file to OneDrive (or storage fallback) and
  // return the resulting URL. Returns null on failure.
  const uploadPhoto = async (file) => {
    const formId = await ensureDraftId();
    if (!formId) return null;
    setPendingUploads(p => p + 1);
    try {
      const res = await uploadFieldReportPhoto('soft-digs', formId, file, token);
      return res?.url || null;
    } catch (err) {
      console.error('Photo upload failed:', err);
      toast.error('Photo upload failed. Check your connection and try again.');
      return null;
    } finally {
      setPendingUploads(p => Math.max(0, p - 1));
    }
  };

  // Upload a base64 signature data URL and return the resulting URL.
  const uploadSignature = async (dataUrl) => {
    const formId = await ensureDraftId();
    if (!formId) return null;
    try {
      const res = await uploadFieldReportSignature('soft-digs', formId, dataUrl, token);
      return res?.url || null;
    } catch (err) {
      console.error('Signature upload failed:', err);
      toast.error('Signature upload failed');
      return null;
    }
  };

  // Fire-and-forget OneDrive cleanup when a photo is removed from the
  // soft-dig structure. Soft-dig photos can live in two nested arrays
  // (soft_digs[].additional_photos and soft_digs[].reference_points[].
  // attachments) — the backend endpoint walks both and deletes from
  // whichever it finds. Caller passes the photo dict so we can skip
  // calling for placeholder rows that never got uploaded (no .url).
  const deletePhotoFromBackend = (photo) => {
    if (!photo || !photo.id || !photo.url) return;     // unsaved placeholder
    if (!currentId) return;                             // no record yet
    axios.delete(`${API}/api/soft-digs/${currentId}/photos/${photo.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => { /* non-fatal; UI is already updated */ });
  };

  const loadSoftDig = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await axios.get(`${API}/api/soft-digs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const softDigData = normalizeSoftDigPayload(response.data);
      
      // Find and set the selected job if editing
      if (softDigData.job_id) {
        const job = availableJobs.find(j => j.id === softDigData.job_id);
        if (job) {
          setSelectedJob(job);
          // Update assigned_team from current job data (not stale stored value)
          softDigData.assigned_team = job.assigned_team_names?.join(', ') || softDigData.assigned_team || '';
        }
      }
      
      setFormData(softDigData);
      formLoadedRef.current = true;
    } catch (error) {
      // Only leave the form when the server has definitively said "no" —
      // record truly gone (404) or no permission (403). A network hiccup
      // (timeout, offline, 5xx) must NOT boot the tech back to the list —
      // that looked exactly like "locked out of my form" on a job site
      // with spotty signal. Show an inline retry instead.
      const status = error.response?.status;
      if (status === 404 || status === 403) {
        toast.error(status === 404 ? 'Soft dig report not found' : "You don't have permission to view this report");
        navigate('/field-reports');
      } else {
        setLoadError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = async (isAutosave = false) => {
    if (isViewOnly) return;
    // SAFETY: never PUT empty formData over a real server record before the
    // load finishes, and never un-submit an already-submitted record.
    if (isEditing && !formLoadedRef.current) return;
    if (isEditing && formData.status && formData.status !== 'draft') return;
    
    setSaving(true);
    try {
      const payload = buildSavePayload({ ...formData, status: 'draft' });
      
      let response;
      if (currentId) {
        response = await axios.put(`${API}/api/soft-digs/${currentId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${API}/api/soft-digs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Navigate to edit mode after first save
        const newId = response.data?.id;
        if (newId) {
          setCurrentId(newId);
          navigate(`/soft-dig/${newId}`, { replace: true });
        }
      }
      
      setLastSaved(new Date());
      if (!isAutosave) {
        toast.success('Draft saved');
      }
    } catch (error) {
      if (!isAutosave) {
        toast.error('Failed to save draft');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Client-side gate — every reference point on every dig must have at
    // least 3 attachments. Matches the per-card hint and prevents the
    // backend from saving a partially-completed report.
    const offenders = [];
    (formData.soft_digs || []).forEach((dig, di) => {
      (dig.reference_points || []).forEach((rp, ri) => {
        const atts = Array.isArray(rp.attachments) && rp.attachments.length > 0
          ? rp.attachments
          : (rp.photo_url ? [{ url: rp.photo_url }] : []);
        const filled = atts.filter((a) => a && a.url).length;
        if (filled < 3) offenders.push(`Soft Dig #${di + 1} — Reference Point #${ri + 1} (${filled}/3)`);
      });
    });
    if (offenders.length) {
      // Scroll to top so this warning is visible even though Submit
      // sits at the bottom of a long form (2026-09-09 field report).
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.error(
        <div>
          <p className="font-bold mb-1">Each Reference Point needs at least 3 photos / files:</p>
          <ul className="text-sm list-disc pl-4">
            {offenders.slice(0, 5).map((m, i) => <li key={i}>{m}</li>)}
            {offenders.length > 5 && <li>...and {offenders.length - 5} more</li>}
          </ul>
        </div>,
        { duration: 8000 }
      );
      return;
    }

    setSubmitting(true);
    try {
      // First save
      const payload = buildSavePayload({ ...formData });
      
      if (currentId) {
        await axios.put(`${API}/api/soft-digs/${currentId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const createResponse = await axios.post(`${API}/api/soft-digs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newId = createResponse.data?.id;
        if (newId) setCurrentId(newId);
        // Use the new ID
        await axios.post(`${API}/api/soft-digs/${newId}/submit`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Soft dig report submitted!');
        navigate(`/soft-dig/${newId}?view=true`);
        return;
      }
      
      // Then submit
      await axios.post(`${API}/api/soft-digs/${currentId}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Soft dig report submitted!');
      navigate(`/soft-dig/${currentId}?view=true`);
    } catch (error) {
      // Scroll to top + keep the toast up longer — same visibility gap
      // as the offenders check above (2026-09-09 field report).
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (error.response?.data?.detail?.errors) {
        const errors = error.response.data.detail.errors;
        toast.error(
          <div>
            <p className="font-bold mb-1">Missing required fields:</p>
            <ul className="text-sm list-disc pl-4">
              {errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
              {errors.length > 5 && <li>...and {errors.length - 5} more</li>}
            </ul>
          </div>,
          { duration: 8000 }
        );
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail, { duration: 8000 });
      } else {
        toast.error('Failed to submit report', { duration: 8000 });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!formData.reviewer_signature_url) {
      toast.error('Please sign the Manager Approval signature pad before approving.');
      return;
    }
    setApproving(true);
    try {
      await axios.put(`${API}/api/soft-digs/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await axios.post(`${API}/api/soft-digs/${id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report approved!');
      loadSoftDig();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to approve report';
      toast.error(typeof msg === 'string' ? msg : 'Failed to approve report');
    } finally {
      setApproving(false);
    }
  };

  // Submitter editing a pending-review report — backend enforces signature lock.
  const handleSaveSubmitterEdits = async () => {
    if (!currentId) return;
    setSaving(true);
    try {
      const payload = { ...formData };
      delete payload.status;
      await axios.put(`${API}/api/soft-digs/${currentId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastSaved(new Date());
      toast.success('Changes saved — still pending review.');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const addSoftDig = () => {
    setFormData(prev => ({
      ...prev,
      soft_digs: [...(prev.soft_digs || []), makeEmptyDig()]
    }));
  };

  const updateSoftDig = (index, data) => {
    setFormData(prev => ({
      ...prev,
      soft_digs: (prev.soft_digs || []).map((d, i) => (i === index ? data : d))
    }));
  };

  const removeSoftDig = (index) => {
    setFormData(prev => ({
      ...prev,
      soft_digs: (prev.soft_digs || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmitterSign = (dataUrl, gps) => {
    setFormData(prev => ({
      ...prev,
      submitter_signature_url: dataUrl,
      submitter_gps_lat: gps?.lat,
      submitter_gps_lng: gps?.lng,
      submitter_gps_accuracy: gps?.accuracy,
      submitter_signed_at: dataUrl ? new Date().toISOString() : ''
    }));
  };

  const handleReviewerSign = (dataUrl, gps) => {
    setFormData(prev => ({
      ...prev,
      reviewer_signature_url: dataUrl,
      reviewer_gps_lat: gps?.lat,
      reviewer_gps_lng: gps?.lng,
      reviewer_gps_accuracy: gps?.accuracy,
      reviewer_signed_at: dataUrl ? new Date().toISOString() : ''
    }));
  };

  // Form is disabled until a job is selected (for new forms)
  const isFormDisabled = !isEditing && !formData.job_id;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-6 text-center" data-testid="soft-dig-load-error">
        <AlertTriangle className="w-10 h-10 text-amber-500" />
        <div>
          <p className="font-medium text-slate-900">Couldn't load this report</p>
          <p className="text-sm text-slate-500 mt-1">Check your connection and try again — your form is still there, nothing was lost.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/field-reports')} data-testid="load-error-back-btn">Back to Field Reports</Button>
          <Button onClick={loadSoftDig} data-testid="load-error-retry-btn">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-[calc(11rem+env(safe-area-inset-bottom))] md:pb-32">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/field-reports')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Soft Dig Form</h1>
              <p className="text-xs text-slate-500">
                {formData.job_number ? `Job: ${formData.job_number}` : 'Center Line Locating'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canAdminUnlock && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
                onClick={handleAdminEdit}
                data-testid="admin-edit-report-btn"
                title="Unlock this report to fix a mistake"
              >
                Edit Report
              </Button>
            )}
            {isAdminEditingNonDraft && (
              <Button
                size="sm"
                className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                onClick={handleAdminSaveEdits}
                disabled={saving}
                data-testid="admin-save-edits-btn"
                title="Save changes — does NOT change the report status"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            )}
            {formData.status === 'draft' && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Draft</Badge>
            )}
            {formData.status === 'submitted_pending_review' && (
              <Badge className="bg-blue-100 text-blue-700">Pending Review</Badge>
            )}
            {formData.status === 'completed' && (
              <Badge className="bg-green-100 text-green-700">Completed</Badge>
            )}
          </div>
        </div>
        {lastSaved && (
          <div className="px-4 pb-2 text-xs text-slate-400">
            Last saved: {lastSaved.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Status Banner for submitted/completed forms */}
      {(formData.status === 'submitted_pending_review' || formData.status === 'completed') && (
        <div className={`mx-4 mt-4 p-4 rounded-lg ${formData.status === 'completed' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
          <div className="flex items-center gap-3">
            {formData.status === 'completed' ? (
              <Check className="w-6 h-6 text-green-600" />
            ) : (
              <AlertCircle className="w-6 h-6 text-blue-600" />
            )}
            <div>
              <p className={`font-semibold ${formData.status === 'completed' ? 'text-green-800' : 'text-blue-800'}`}>
                {formData.status === 'completed' ? 'Report Completed' : 'Submitted - Pending Review'}
              </p>
              <p className={`text-sm ${formData.status === 'completed' ? 'text-green-600' : 'text-blue-600'}`}>
                {formData.status === 'completed' 
                  ? `Reviewed by ${formData.reviewed_by || 'Manager'} on ${formData.reviewed_at ? new Date(formData.reviewed_at).toLocaleDateString() : 'N/A'}`
                  : `Submitted by ${formData.submitted_by || formData.cll_lead_on_site} on ${formData.submitted_at ? new Date(formData.submitted_at).toLocaleDateString() : 'N/A'}`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 space-y-6 max-w-2xl mx-auto">
        {/* Job Selection — Feb 2026 audit: swapped the byte-identical
            hand-rolled block for the shared <JobSelectionCard>. Zero
            visible UX change; keeps this form in lockstep with PreCon
            and CSE for future updates. */}
        <JobSelectionCard
          value={formData.job_id}
          onChange={handleJobSelect}
          availableJobs={availableJobs}
          loadingJobs={loadingJobs}
          selectedJob={selectedJob}
          disabled={isViewOnly || isEditing}
          testIdPrefix="softdig-job-selection"
        />

        {/* Warning if no job selected */}
        {isFormDisabled && (
          <div className="bg-amber-100 border border-amber-300 rounded-lg p-4 text-center">
            <AlertCircle className="w-6 h-6 mx-auto text-amber-600 mb-2" />
            <p className="text-amber-800 font-medium">Please select a job to continue</p>
            <p className="text-sm text-amber-600">The form will be enabled once a job is selected</p>
          </div>
        )}

        {/* Section A: Basic Info */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">A</span>
              Soft Dig Form Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Assigned Team <span className="text-slate-400 text-xs">(from Job - read only)</span></Label>
              <Input
                value={formData.assigned_team}
                placeholder="Auto-filled from job"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Site Location <span className="text-slate-400 text-xs">(from Job - read only)</span></Label>
              <Input
                value={formData.site_location}
                placeholder="Auto-filled from job"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Date <span className="text-red-500">*</span> <span className="text-slate-400 text-xs">(editable)</span></Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => updateField('date', e.target.value)}
                disabled={isViewOnly}
              />
            </div>
            <div>
              <Label>CLL Lead on Site <span className="text-slate-400 text-xs">(from logged-in user)</span></Label>
              <Input
                value={formData.cll_lead_on_site}
                placeholder="Auto-filled from logged-in user"
                disabled
                className="bg-slate-100"
              />
            </div>
          </CardContent>
        </Card>

        {/* Sections B–E: Multi-Soft-Dig Entries */}
        <div className={isFormDisabled ? 'opacity-50 pointer-events-none space-y-4' : 'space-y-4'}>
          {(formData.soft_digs || []).map((dig, idx) => (
            <SoftDigEntryCard
              key={dig.id}
              dig={dig}
              index={idx}
              totalCount={(formData.soft_digs || []).length}
              onChange={(data) => updateSoftDig(idx, data)}
              onRemove={() => removeSoftDig(idx)}
              canRemove={(formData.soft_digs || []).length > 1}
              disabled={isViewOnly}
              onUploadFile={uploadPhoto}
              onDeletePhoto={deletePhotoFromBackend}
            />
          ))}

          {!isViewOnly && (
            <Button
              type="button"
              variant="outline"
              className="w-full border-amber-500 text-amber-700 hover:bg-amber-50 h-12"
              onClick={addSoftDig}
              data-testid="add-soft-dig-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Soft Dig
            </Button>
          )}
        </div>

        {/* Section F: Signatures */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">F</span>
              Submission & Signatures
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Instructions for form submission */}
            {formData.status === 'draft' && !canApproveFieldReports() && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
                <p className="font-medium mb-1">Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Sign your signature below</li>
                  <li>Click the green "Submit for Approval" button</li>
                  <li>Your manager will review and approve the form</li>
                </ol>
              </div>
            )}
            
            {/* Show rejection reason if form was rejected */}
            {formData.rejection_reason && formData.status === 'draft' && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-sm text-red-700">
                <p className="font-medium">Form was rejected:</p>
                <p>{formData.rejection_reason}</p>
                <p className="text-xs mt-1">Rejected by {formData.rejected_by} on {formData.rejected_at ? new Date(formData.rejected_at).toLocaleDateString() : 'N/A'}</p>
              </div>
            )}

            <div>
              <Label>Submitted By</Label>
              <Input
                value={formData.submitted_by}
                onChange={(e) => updateField('submitted_by', e.target.value)}
                disabled
              />
            </div>

            {/* Submitter Signature - Always visible */}
            <SignaturePad
              label="Submitter Signature"
              signatureUrl={formData.submitter_signature_url}
              onSign={handleSubmitterSign}
              gpsData={{
                lat: formData.submitter_gps_lat,
                lng: formData.submitter_gps_lng,
                accuracy: formData.submitter_gps_accuracy
              }}
              name={formData.submitter_name}
              onNameChange={(v) => updateField('submitter_name', v)}
              signedAt={formData.submitter_signed_at}
              required
              disabled={isViewOnly || formData.status !== 'draft'}
              onUploadSignature={uploadSignature}
            />

            {/* Reviewer Signature - Only visible to users with approval permission when form is submitted */}
            {(canApproveFieldReports() && formData.status === 'submitted_pending_review') && (
              <SignaturePad
                label="Manager Approval Signature"
                signatureUrl={formData.reviewer_signature_url}
                onSign={handleReviewerSign}
                gpsData={{
                  lat: formData.reviewer_gps_lat,
                  lng: formData.reviewer_gps_lng,
                  accuracy: formData.reviewer_gps_accuracy
                }}
                name={formData.reviewer_name}
                onNameChange={(v) => updateField('reviewer_name', v)}
                signedAt={formData.reviewer_signed_at}
                required={false}
                disabled={false}
                onUploadSignature={uploadSignature}
              />
            )}
            
            {/* Show reviewer signature (read-only) for completed forms */}
            {formData.status === 'completed' && formData.reviewer_signature_url && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <Label className="text-green-700">Manager Approval Signature</Label>
                <img src={formData.reviewer_signature_url} alt="Manager Signature" className="max-h-24 mt-2" />
                <p className="text-xs text-green-600 mt-1">
                  Approved by {formData.reviewer_name || formData.reviewed_by} on {formData.reviewer_signed_at ? new Date(formData.reviewer_signed_at).toLocaleDateString() : (formData.reviewed_at ? new Date(formData.reviewed_at).toLocaleDateString() : 'N/A')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fixed Bottom Actions - Only show for draft forms */}
      {!isViewOnly && formData.status === 'draft' && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <div className="px-4 pt-2 pb-1 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate"><strong>Tip:</strong> Save Draft often — Submit will also auto-save your latest changes.</span>
            </div>
            <LastSavedIndicator lastSaved={lastSaved} saving={saving} />
          </div>
          <div className="p-4 flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => handleSaveDraft(false)}
            disabled={saving || pendingUploads > 0}
            data-testid="save-draft-btn"
            title={pendingUploads > 0 ? 'Wait for photo uploads to finish' : ''}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {pendingUploads > 0 ? 'Uploading photos…' : 'Save Draft'}
          </Button>
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700"
            onClick={handleSubmit}
            disabled={submitting || pendingUploads > 0}
            data-testid="submit-report-btn"
            title={pendingUploads > 0 ? 'Wait for photo uploads to finish' : ''}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {pendingUploads > 0 ? 'Uploading photos…' : 'Submit Report'}
          </Button>
          </div>
        </div>
      )}
      
      {/* Submitter Edit bar — pending-review report edited by its original
          submitter. Approvers see the Approve/Reject bar instead. */}
      {!isViewOnly && isPending && isSubmitter && !canApproveFieldReports() && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <div className="px-4 pt-2 pb-1 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate"><strong>Pending Review</strong> — you can edit until the reviewer approves. Your signature is locked.</span>
            </div>
            <LastSavedIndicator lastSaved={lastSaved} saving={saving} />
          </div>
          <div className="p-4">
            {photosNotReady && (
              <div className="mb-3 p-2 rounded border border-amber-300 bg-amber-50 text-amber-800 text-xs flex items-start gap-2" data-testid="photo-not-ready-banner-submitter">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{photoBannerText || `${pendingUploads} photo upload${pendingUploads === 1 ? '' : 's'} still in flight — wait for them to finish before saving.`}</span>
              </div>
            )}
            <Button
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleSaveSubmitterEdits}
              disabled={saving || pendingUploads > 0}
              data-testid="submitter-save-changes-btn"
              title={pendingUploads > 0 ? 'Wait for photo uploads to finish' : ''}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {pendingUploads > 0 ? 'Uploading photos…' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}

      {/* Admin Actions for submitted forms */}
      {canAdminEdit && formData.status === 'submitted_pending_review' && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t p-4 max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          {photosNotReady && (
            <div
              className="mb-3 p-2 rounded border border-amber-300 bg-amber-50 text-amber-800 text-xs flex items-start gap-2"
              data-testid="photo-not-ready-banner"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div>{photoBannerText || `${pendingUploads} photo upload${pendingUploads === 1 ? '' : 's'} still in flight — please wait before approving.`}</div>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/api/soft-digs/${id}/recompute-photo-counts`, {}, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      toast.success('Photo counts reconciled');
                      const refreshed = await axios.get(`${API}/api/soft-digs/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      setFormData(refreshed.data);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to recompute counts');
                    }
                  }}
                  className="mt-1 text-amber-900 underline font-semibold hover:text-amber-700"
                  data-testid="recompute-photo-counts-btn"
                >
                  Photos look right? → Recompute counts &amp; unlock approval
                </button>
              </div>
            </div>
          )}
          <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
            onClick={async () => {
              const reason = window.prompt('Please enter a rejection reason — the submitter will receive this in their notification and email:');
              if (reason === null) return; // User cancelled
              if (!reason.trim()) {
                toast.error('A rejection reason is required so the submitter knows what to fix.');
                return;
              }
              try {
                await axios.post(`${API}/api/soft-digs/${id}/reject?rejection_reason=${encodeURIComponent(reason)}`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Report rejected — submitter notified by email and in-app.');
                navigate('/field-reports');
              } catch (error) {
                toast.error('Failed to reject report');
              }
            }}
            data-testid="reject-report-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700"
            onClick={handleApprove}
            disabled={approving || photosNotReady}
            data-testid="approve-report-btn"
            title={photosNotReady ? 'Photos still uploading — wait until all are received.' : ''}
          >
            {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Approve Report
          </Button>
          </div>
        </div>
      )}
      
      {/* Print / Save PDF Button for non-admin viewing submitted/completed OR for completed status */}
      {((isViewOnly && !canAdminEdit) || formData.status === 'completed') && (formData.status === 'submitted_pending_review' || formData.status === 'completed') && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t p-4 flex gap-3 max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <Button
            className="flex-1 bg-orange-600 hover:bg-orange-700"
            onClick={() => navigate(`/soft-dig/${id}/preview`)}
            data-testid="print-preview-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Print / Save PDF
          </Button>
        </div>
      )}
    </div>
  );
}
